import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  defaultReferencePresentationResolver,
  type ReferencePresentationResolver,
} from "../mentions";

export interface MentionItem {
  id: string;
  label: string;
  mentionType: string;
  subtitle?: string;
  iconKey: string;
}

interface MentionListProps {
  items: MentionItem[];
  command: (item: MentionItem) => void;
  resolvePresentation?: ReferencePresentationResolver;
}

function MentionListRow({
  item,
  selected,
  onSelect,
  resolvePresentation,
}: {
  item: MentionItem;
  selected: boolean;
  onSelect: () => void;
  resolvePresentation: ReferencePresentationResolver;
}) {
  const Icon = resolvePresentation({
    kind: "mention",
    mentionType: item.mentionType,
    iconKey: item.iconKey,
  }).Icon;

  return (
    <button
      type="button"
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${
        selected ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
      }`}
      onClick={onSelect}
    >
      {Icon ? <Icon className="size-3 shrink-0" aria-hidden="true" /> : null}
      <span className="truncate">{item.label}</span>
      {item.subtitle ? (
        <span className="ml-auto truncate text-[11px] text-muted-foreground">{item.subtitle}</span>
      ) : null}
    </button>
  );
}

/**
 * Mention suggestion dropdown rendered by Tiptap's suggestion plugin.
 *
 * Tiptap calls this component via its render lifecycle and expects
 * `onKeyDown` to be exposed via ref so it can forward keyboard events.
 */
export const MentionList = forwardRef<
  { onKeyDown: (props: { event: KeyboardEvent }) => boolean },
  MentionListProps
>(({ items, command, resolvePresentation = defaultReferencePresentationResolver }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemSignature = items.map((item) => item.id).join("|");

  // Reset selection when items change (new filter results)
  useEffect(() => {
    void itemSignature;
    setSelectedIndex(0);
  }, [itemSignature]);

  function selectItem(index: number) {
    const item = items[index];
    if (item) {
      command(item);
    }
  }

  // Expose keyboard handler to Tiptap's suggestion plugin
  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (items.length === 0) {
        return false;
      }

      if (event.key === "ArrowUp") {
        setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
        return true;
      }

      if (event.key === "ArrowDown") {
        setSelectedIndex((prev) => (prev + 1) % items.length);
        return true;
      }

      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        selectItem(selectedIndex);
        return true;
      }

      return false;
    },
  }));

  if (items.length === 0) {
    return (
      <div className="bg-popover text-popover-foreground border rounded-md px-3 py-2 text-xs text-muted-foreground shadow-md">
        No results
      </div>
    );
  }

  return (
    <div className="bg-popover text-popover-foreground border rounded-md py-1 shadow-md overflow-y-auto max-h-[var(--mention-popup-max-height,18rem)] overscroll-contain">
      {items.map((item, index) => (
        <MentionListRow
          key={item.id}
          item={item}
          selected={index === selectedIndex}
          onSelect={() => selectItem(index)}
          resolvePresentation={resolvePresentation}
        />
      ))}
    </div>
  );
});
